import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Alert, Badge, Button, Card, Col, Form, Modal, Row, Spinner, Tab, Table, Tabs } from 'react-bootstrap';
import PageHeader from '../components/PageHeader';
import CandidateAutocomplete from '../components/CandidateAutocomplete';
import { adminApi, extractError } from '../api/client';
import { formatDateUS } from '../utils/dates';
import { useToast } from '../contexts/ToastContext';

const STATUS_VARIANT = {
  scheduled: 'info', active: 'primary', completed: 'success', canceled: 'secondary',
};

const DEADLINE_TYPES = [
  { value: 'election_day', label: 'Election Day' },
  { value: 'registration_deadline', label: 'Registration Deadline' },
  { value: 'early_voting_start', label: 'Early Voting Start' },
  { value: 'early_voting_end', label: 'Early Voting End' },
  { value: 'vote_by_mail_request_deadline', label: 'Vote-by-Mail Request' },
  { value: 'vote_by_mail_return_deadline', label: 'Vote-by-Mail Return' },
  { value: 'candidate_filing_deadline', label: 'Candidate Filing' },
];

const BALLOT_STATUSES = ['filed', 'qualified', 'active', 'withdrawn', 'disqualified'];
const RESULT_STATUSES = ['unknown', 'lost', 'won', 'advanced', 'elected'];
const PARTY_SCOPES = ['republican', 'democratic', 'nonpartisan', 'all', 'other'];

export default function ElectionDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [election, setElection] = useState(null);
  const [deadlines, setDeadlines] = useState([]);
  const [races, setRaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [counties, setCounties] = useState([]);
  const [offices, setOffices] = useState([]);

  // Deadline modal
  const [showDeadline, setShowDeadline] = useState(false);
  const [deadlineForm, setDeadlineForm] = useState({ type: 'election_day', date: '', county_id: '' });

  // Race modal
  const [showRace, setShowRace] = useState(false);
  const [raceForm, setRaceForm] = useState({ office_id: '', jurisdiction_id: '', district: '', seat: '', party_scope: 'all' });

  // Race candidates state — keyed by race_id
  const [selectedRace, setSelectedRace] = useState(null);
  const [raceCandidates, setRaceCandidates] = useState([]);
  const [showAddCandidate, setShowAddCandidate] = useState(false);
  const [addCandidateId, setAddCandidateId] = useState('');
  const [addCandidateParty, setAddCandidateParty] = useState('');
  const [addCandidateIncumbent, setAddCandidateIncumbent] = useState(false);

  // Copy-to-race state
  const [copyRC, setCopyRC] = useState(null);
  const [copyTargetRaceId, setCopyTargetRaceId] = useState('');
  const [copyableRaces, setCopyableRaces] = useState([]);
  const [copyKeepParty, setCopyKeepParty] = useState(true);
  const [copyKeepIncumbent, setCopyKeepIncumbent] = useState(false);
  const [copySaving, setCopySaving] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const [eRes, dRes, rRes] = await Promise.all([
        adminApi.get(`/admin/elections/${id}`),
        adminApi.get(`/admin/elections/${id}/deadlines`),
        adminApi.get(`/admin/elections/${id}/races`),
      ]);
      setElection(eRes.data);
      setDeadlines(dRes.data.items || []);
      setRaces(rRes.data.items || []);
      // load counties for this election's state (Phase A: use jurisdictions endpoint)
      if (eRes.data.state_id) {
        // Find the state jurisdiction first
        const stateJurisdictions = await adminApi.get(`/admin/jurisdictions?type=state&state_id=${eRes.data.state_id}&page_size=10`);
        const stateJurisdiction = stateJurisdictions.data.items?.[0];
        if (stateJurisdiction) {
          // Get county jurisdictions under this state
          const countyJurisdictions = await adminApi.get(`/admin/jurisdictions?type=county&parent_jurisdiction_id=${stateJurisdiction.id}&page_size=500`);
          // Map to counties format (extract county_id and name)
          const counties = countyJurisdictions.data.items?.map(cj => ({
            id: cj.county_id,
            name: cj.name,
            slug: cj.slug
          })) || [];
          setCounties(counties);
        }
      }
      // load offices catalog
      const oRes = await adminApi.get('/admin/offices?page_size=500');
      setOffices(oRes.data.items || []);
    } catch (e) {
      setError(extractError(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  async function saveDeadline() {
    if (!deadlineForm.date) { toast.error('Date is required'); return; }
    try {
      await adminApi.post(`/admin/elections/${id}/deadlines`, {
        election_id: id,
        type: deadlineForm.type,
        date: deadlineForm.date,
        county_id: deadlineForm.county_id || null,
      });
      setShowDeadline(false);
      setDeadlineForm({ type: 'election_day', date: '', county_id: '' });
      toast.success('Deadline added');
      load();
    } catch (e) { toast.error(extractError(e)); }
  }

  async function deleteDeadline(deadlineId) {
    if (!window.confirm('Delete this deadline?')) return;
    try {
      await adminApi.delete(`/admin/elections/${id}/deadlines/${deadlineId}`);
      load();
    } catch (e) { toast.error(extractError(e)); }
  }

  async function saveRace() {
    if (!raceForm.office_id) { toast.error('Office is required'); return; }
    const jid = raceForm.jurisdiction_id || election.jurisdiction_id;
    try {
      await adminApi.post(`/admin/elections/${id}/races`, {
        election_id: id,
        office_id: raceForm.office_id,
        jurisdiction_id: jid,
        district: raceForm.district || '',
        seat: raceForm.seat || '',
        party_scope: raceForm.party_scope,
      });
      setShowRace(false);
      setRaceForm({ office_id: '', jurisdiction_id: '', district: '', seat: '', party_scope: 'all' });
      toast.success('Race added');
      load();
    } catch (e) { toast.error(extractError(e)); }
  }

  async function openRaceCandidates(race) {
    setSelectedRace(race);
    try {
      const r = await adminApi.get(`/admin/elections/${id}/races/${race.id}/candidates`);
      setRaceCandidates(r.data.items || []);
    } catch (e) { toast.error(extractError(e)); }
  }

  async function addRaceCandidate() {
    if (!addCandidateId) { toast.error('Pick a candidate'); return; }
    try {
      await adminApi.post(`/admin/elections/${id}/races/${selectedRace.id}/candidates`, {
        race_id: selectedRace.id,
        candidate_id: addCandidateId,
        party: addCandidateParty || '',
        incumbent: addCandidateIncumbent,
        ballot_status: 'filed',
        result_status: 'unknown',
      });
      setAddCandidateId('');
      setAddCandidateParty('');
      setAddCandidateIncumbent(false);
      setShowAddCandidate(false);
      toast.success('Candidate added to race');
      openRaceCandidates(selectedRace);
      load();
    } catch (e) { toast.error(extractError(e)); }
  }

  async function updateRC(rc, patch) {
    try {
      await adminApi.put(`/admin/elections/${id}/races/${selectedRace.id}/candidates/${rc.id}`, patch);
      openRaceCandidates(selectedRace);
    } catch (e) { toast.error(extractError(e)); }
  }

  async function removeRC(rc) {
    if (!window.confirm('Remove this candidate from the race?')) return;
    try {
      await adminApi.delete(`/admin/elections/${id}/races/${selectedRace.id}/candidates/${rc.id}`);
      openRaceCandidates(selectedRace);
      load();
    } catch (e) { toast.error(extractError(e)); }
  }

  async function openCopyDialog(rc) {
    setCopyRC(rc);
    setCopyTargetRaceId('');
    setCopyKeepParty(true);
    setCopyKeepIncumbent(false);
    // Load a flat list of all races across all elections (limit to a reasonable batch)
    try {
      // Pull all elections (small set) and their races
      const er = await adminApi.get('/admin/elections?page_size=200');
      const allRaces = [];
      for (const e of (er.data.items || [])) {
        const rr = await adminApi.get(`/admin/elections/${e.id}/races`);
        for (const r of (rr.data.items || [])) {
          if (r.id === selectedRace.id) continue;  // skip source race
          allRaces.push({
            id: r.id,
            label: `${e.date} · ${e.name} · ${r.office?.name || ''}${r.district ? ' ('+r.district+')' : ''}`,
          });
        }
      }
      setCopyableRaces(allRaces);
    } catch (e) {
      setCopyableRaces([]);
      toast.error('Failed to load destination races');
    }
  }

  async function saveCopy() {
    if (!copyTargetRaceId) { toast.error('Pick a destination race'); return; }
    setCopySaving(true);
    try {
      await adminApi.post(
        `/admin/elections/${id}/races/${selectedRace.id}/candidates/${copyRC.id}/copy-to-race`,
        {
          target_race_id: copyTargetRaceId,
          keep_party: copyKeepParty,
          keep_incumbent: copyKeepIncumbent,
          initial_ballot_status: 'filed',
          initial_result_status: 'unknown',
        },
      );
      toast.success('Candidate copied to target race');
      setCopyRC(null);
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setCopySaving(false);
    }
  }

  async function deleteElection() {
    if (!window.confirm('Delete this election (and all its races, candidates, deadlines)?')) return;
    try {
      await adminApi.delete(`/admin/elections/${id}?force=true`);
      toast.success('Election deleted');
      navigate('/admin/elections');
    } catch (e) { toast.error(extractError(e)); }
  }

  if (loading) return <><PageHeader title="Election" /><div className="text-center p-5"><Spinner animation="border" /></div></>;
  if (error || !election) return <><PageHeader title="Election" /><div className="page-content"><Alert variant="danger">{error || 'Not found'}</Alert></div></>;

  return (
    <>
      <PageHeader
        title={election.name}
        breadcrumb={<span><Link to="/admin/elections" className="text-decoration-none">Election Days</Link> / Detail</span>}
        actions={
          <div className="d-flex gap-2">
            {election.type === 'primary' && (
              <Button
                as={Link}
                to={`/admin/elections/${id}/reconcile`}
                variant="warning"
                data-testid="election-reconcile-button"
              >
                <i className="bi bi-trophy me-1" />Reconcile Primary
              </Button>
            )}
            <Button as={Link} to={`/admin/elections/${id}/edit`} variant="outline-secondary" data-testid="election-edit-button"><i className="bi bi-pencil me-1" />Edit</Button>
            <Button variant="outline-danger" onClick={deleteElection} data-testid="election-delete-button"><i className="bi bi-trash me-1" />Delete</Button>
          </div>
        }
      />
      <div className="page-content">
        <Card className="mb-3">
          <Card.Body>
            <Row className="g-3">
              <Col md={3}><div className="text-muted small">Date</div><code className="fs-5">{formatDateUS(election.date)}</code></Col>
              <Col md={3}><div className="text-muted small">Type</div><Badge bg="primary" className="fs-6">{election.type}</Badge></Col>
              <Col md={3}><div className="text-muted small">Jurisdiction</div><div className="fw-semibold">{election.jurisdiction?.name}</div></Col>
              <Col md={3}><div className="text-muted small">Status</div><Badge bg={STATUS_VARIANT[election.status_computed]} className="fs-6">{election.status_computed}</Badge></Col>
            </Row>
            {election.description && <div className="mt-3 text-muted">{election.description}</div>}
          </Card.Body>
        </Card>

        <Tabs defaultActiveKey="deadlines" className="mb-3" data-testid="election-tabs">
          <Tab eventKey="deadlines" title={`Deadlines (${deadlines.length})`}>
            <Card>
              <Card.Header className="d-flex justify-content-between">
                <strong>Deadlines &amp; key dates</strong>
                <Button size="sm" variant="primary" onClick={() => setShowDeadline(true)} data-testid="add-deadline-button"><i className="bi bi-plus-lg me-1" />Add deadline</Button>
              </Card.Header>
              {deadlines.length === 0 ? (
                <div className="empty-state"><i className="bi bi-calendar-event" /><div>No deadlines yet.</div></div>
              ) : (
                <Table size="sm" responsive hover className="mb-0">
                  <thead><tr><th>Date</th><th>Type</th><th>County</th><th className="text-end">Actions</th></tr></thead>
                  <tbody>
                    {deadlines.map((d) => (
                      <tr key={d.id} data-testid={`deadline-row-${d.id}`}>
                        <td><code>{formatDateUS(d.date)}</code></td>
                        <td>{(d.type || '').replace(/_/g, ' ')}</td>
                        <td>{d.county?.name || <span className="text-muted">statewide</span>}</td>
                        <td className="text-end">
                          <Button size="sm" variant="outline-danger" onClick={() => deleteDeadline(d.id)} data-testid={`deadline-delete-${d.id}`}><i className="bi bi-trash" /></Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card>
          </Tab>
          <Tab eventKey="races" title={`Races (${races.length})`}>
            <Card>
              <Card.Header className="d-flex justify-content-between">
                <strong>Races on the ballot</strong>
                <Button size="sm" variant="primary" onClick={() => setShowRace(true)} data-testid="add-race-button"><i className="bi bi-plus-lg me-1" />Add race</Button>
              </Card.Header>
              {races.length === 0 ? (
                <div className="empty-state"><i className="bi bi-bank" /><div>No races yet.</div></div>
              ) : (
                <Table size="sm" responsive hover className="mb-0">
                  <thead><tr><th>Office</th><th>District / Seat</th><th>Party scope</th><th className="text-end">Candidates</th><th className="text-end">Actions</th></tr></thead>
                  <tbody>
                    {races.map((r) => (
                      <tr key={r.id} data-testid={`race-row-${r.id}`}>
                        <td className="fw-semibold">{r.office?.name || '—'}</td>
                        <td>{r.district || ''} {r.seat ? ` · ${r.seat}` : ''}</td>
                        <td><Badge bg="light" text="dark">{r.party_scope}</Badge></td>
                        <td className="text-end">{r.race_candidates_count || 0}</td>
                        <td className="text-end">
                          <Button size="sm" variant="outline-primary" onClick={() => openRaceCandidates(r)} data-testid={`race-candidates-${r.id}`}>Candidates</Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card>
          </Tab>
        </Tabs>

        {/* Add Deadline modal */}
        <Modal show={showDeadline} onHide={() => setShowDeadline(false)} centered>
          <Modal.Header closeButton><Modal.Title>Add deadline</Modal.Title></Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-2"><Form.Label>Type</Form.Label>
              <Form.Select value={deadlineForm.type} onChange={(e) => setDeadlineForm((f) => ({ ...f, type: e.target.value }))} data-testid="deadline-type-select">
                {DEADLINE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-2"><Form.Label>Date</Form.Label>
              <Form.Control type="date" value={deadlineForm.date} onChange={(e) => setDeadlineForm((f) => ({ ...f, date: e.target.value }))} data-testid="deadline-date-input" />
            </Form.Group>
            <Form.Group className="mb-2"><Form.Label>County (optional — leave blank for statewide)</Form.Label>
              <Form.Select value={deadlineForm.county_id} onChange={(e) => setDeadlineForm((f) => ({ ...f, county_id: e.target.value }))} data-testid="deadline-county-select">
                <option value="">— Statewide —</option>
                {counties.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </Form.Select>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setShowDeadline(false)}>Cancel</Button>
            <Button variant="primary" onClick={saveDeadline} data-testid="deadline-save-button">Save</Button>
          </Modal.Footer>
        </Modal>

        {/* Add Race modal */}
        <Modal show={showRace} onHide={() => setShowRace(false)} centered>
          <Modal.Header closeButton><Modal.Title>Add race</Modal.Title></Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-2"><Form.Label>Office</Form.Label>
              <Form.Select value={raceForm.office_id} onChange={(e) => setRaceForm((f) => ({ ...f, office_id: e.target.value }))} data-testid="race-office-select">
                <option value="">— Select office —</option>
                {offices.map((o) => <option key={o.id} value={o.id}>{o.name} ({o.level})</option>)}
              </Form.Select>
              {offices.length === 0 && <Form.Text className="text-warning">No offices defined yet. <Link to="/admin/offices">Create one</Link> first.</Form.Text>}
            </Form.Group>
            <Row>
              <Col><Form.Group className="mb-2"><Form.Label>District (optional)</Form.Label><Form.Control value={raceForm.district} onChange={(e) => setRaceForm((f) => ({ ...f, district: e.target.value }))} data-testid="race-district-input" /></Form.Group></Col>
              <Col><Form.Group className="mb-2"><Form.Label>Seat (optional)</Form.Label><Form.Control value={raceForm.seat} onChange={(e) => setRaceForm((f) => ({ ...f, seat: e.target.value }))} data-testid="race-seat-input" /></Form.Group></Col>
            </Row>
            <Form.Group className="mb-2"><Form.Label>Party scope</Form.Label>
              <Form.Select value={raceForm.party_scope} onChange={(e) => setRaceForm((f) => ({ ...f, party_scope: e.target.value }))} data-testid="race-party-scope-select">
                {PARTY_SCOPES.map((p) => <option key={p} value={p}>{p}</option>)}
              </Form.Select>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setShowRace(false)}>Cancel</Button>
            <Button variant="primary" onClick={saveRace} data-testid="race-save-button">Save</Button>
          </Modal.Footer>
        </Modal>

        {/* Race candidates modal */}
        <Modal show={!!selectedRace} onHide={() => setSelectedRace(null)} size="lg" centered>
          <Modal.Header closeButton><Modal.Title>{selectedRace?.office?.name} — Candidates</Modal.Title></Modal.Header>
          <Modal.Body>
            <div className="d-flex justify-content-end mb-2">
              <Button size="sm" variant="primary" onClick={() => setShowAddCandidate(true)} data-testid="add-race-candidate-button"><i className="bi bi-plus-lg me-1" />Add candidate</Button>
            </div>
            {raceCandidates.length === 0 ? (
              <div className="empty-state"><i className="bi bi-people" /><div>No candidates linked.</div></div>
            ) : (
              <Table size="sm" responsive hover>
                <thead><tr><th>Name</th><th>Party</th><th>Ballot</th><th>Result</th><th>Votes</th><th></th></tr></thead>
                <tbody>
                  {raceCandidates.map((rc) => (
                    <tr key={rc.id} data-testid={`rc-row-${rc.id}`}>
                      <td className="fw-semibold">{rc.candidate?.full_name || '—'}</td>
                      <td>{rc.party}{rc.incumbent ? <Badge bg="info" className="ms-1">incumbent</Badge> : null}</td>
                      <td>
                        <Form.Select size="sm" value={rc.ballot_status} onChange={(e) => updateRC(rc, { ballot_status: e.target.value })} data-testid={`rc-ballot-${rc.id}`}>
                          {BALLOT_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                        </Form.Select>
                      </td>
                      <td>
                        <Form.Select size="sm" value={rc.result_status} onChange={(e) => updateRC(rc, { result_status: e.target.value })} data-testid={`rc-result-${rc.id}`}>
                          {RESULT_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                        </Form.Select>
                      </td>
                      <td><Form.Control size="sm" type="number" value={rc.vote_total ?? ''} onChange={(e) => updateRC(rc, { vote_total: e.target.value ? Number(e.target.value) : null })} style={{ width: 100 }} data-testid={`rc-votes-${rc.id}`} /></td>
                      <td className="text-end">
                        <Button size="sm" variant="outline-primary" className="me-1" onClick={() => openCopyDialog(rc)} data-testid={`rc-copy-${rc.id}`} title="Copy to another race"><i className="bi bi-copy" /></Button>
                        <Button size="sm" variant="outline-danger" onClick={() => removeRC(rc)} data-testid={`rc-remove-${rc.id}`}><i className="bi bi-x" /></Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Modal.Body>
        </Modal>

        {/* Add candidate to race modal */}
        <Modal show={showAddCandidate} onHide={() => setShowAddCandidate(false)} centered>
          <Modal.Header closeButton><Modal.Title>Add candidate to race</Modal.Title></Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-2"><Form.Label>Candidate</Form.Label>
              <CandidateAutocomplete onSelect={(c) => setAddCandidateId(c.id)} placeholder="Type to search…" />
            </Form.Group>
            <Form.Group className="mb-2"><Form.Label>Party</Form.Label>
              <Form.Control value={addCandidateParty} onChange={(e) => setAddCandidateParty(e.target.value)} placeholder="Republican, Democratic, …" data-testid="add-rc-party-input" />
            </Form.Group>
            <Form.Check type="checkbox" label="Incumbent" checked={addCandidateIncumbent} onChange={(e) => setAddCandidateIncumbent(e.target.checked)} data-testid="add-rc-incumbent" />
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setShowAddCandidate(false)}>Cancel</Button>
            <Button variant="primary" onClick={addRaceCandidate} data-testid="add-rc-save-button">Add</Button>
          </Modal.Footer>
        </Modal>

        {/* Copy candidate to another race modal */}
        <Modal show={!!copyRC} onHide={() => setCopyRC(null)} centered>
          <Modal.Header closeButton><Modal.Title>Copy candidate to another race</Modal.Title></Modal.Header>
          <Modal.Body>
            <p className="text-muted" style={{ fontSize: '0.9rem' }}>
              Creating a new RaceCandidate for <strong>{copyRC?.candidate?.full_name}</strong> in the destination race. The original record stays in this race.
            </p>
            <Form.Group className="mb-2"><Form.Label>Destination race</Form.Label>
              <Form.Select value={copyTargetRaceId} onChange={(e) => setCopyTargetRaceId(e.target.value)} data-testid="copy-target-race-select">
                <option value="">— Select —</option>
                {copyableRaces.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
              </Form.Select>
              {copyableRaces.length === 0 && <Form.Text className="text-warning">No other races available.</Form.Text>}
            </Form.Group>
            <Form.Check type="checkbox" className="mt-2" label={`Keep party ("${copyRC?.party || ''}")`} checked={copyKeepParty} onChange={(e) => setCopyKeepParty(e.target.checked)} data-testid="copy-keep-party" />
            <Form.Check type="checkbox" label="Keep incumbent flag" checked={copyKeepIncumbent} onChange={(e) => setCopyKeepIncumbent(e.target.checked)} data-testid="copy-keep-incumbent" />
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setCopyRC(null)}>Cancel</Button>
            <Button variant="primary" onClick={saveCopy} disabled={copySaving || !copyTargetRaceId} data-testid="copy-save-button">
              {copySaving ? <Spinner size="sm" animation="border" /> : 'Copy'}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </>
  );
}
